import { useEffect } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { GameKind } from "../../../shared/protocol.js";
import type { AppContext } from "../App.js";
import { useLobbyWs } from "../lib/useLobbyWs.js";
import {
  GAME_BLURBS,
  GAME_LABELS,
  GAME_KINDS,
  seatOptionsFor,
} from "../lib/gameKinds.js";
import { ChatPanel } from "../components/ChatPanel.js";
import { RulesButton } from "../components/RulesSheet.js";
import { Select } from "../components/Select.js";
import { GameKindBadge } from "../components/GameKindBadge.js";
import { Name } from "../components/Name.js";
import { gameByKind } from "../games/registry.js";

export function LobbyPage() {
  const { id } = useParams<{ id: string }>();
  const ctx = useOutletContext<AppContext>();
  const ws = useLobbyWs(id ?? null);
  const navigate = useNavigate();

  const me = ctx.me;
  const lobby = ws.lobby;
  const mySeat = lobby?.seats.findIndex((s) => s?.playerId === me?.id) ?? -1;
  const seated = mySeat >= 0;
  const inGame = lobby?.status === "in_game";

  useEffect(() => {
    if (inGame && id) {
      navigate(`/game/${id}`, { replace: true });
    }
  }, [inGame, id, navigate]);

  // Lobby was closed by server (host disconnected, etc.) → kick back home.
  useEffect(() => {
    if (ws.closedReason) {
      const t = setTimeout(() => navigate("/", { replace: true }), 1800);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [ws.closedReason, navigate]);

  if (ws.closedReason) {
    return (
      <main className="container">
        <div className="card-block">
          <h2 style={{ marginTop: 0 }}>Lobby closed</h2>
          <p className="muted">{ws.closedReason}. Returning home…</p>
        </div>
      </main>
    );
  }

  if (!lobby) {
    return (
      <main className="container">
        <p className="muted">Loading lobby…</p>
        {ws.lastError && <div className="toast">{ws.lastError}</div>}
      </main>
    );
  }

  const allReady = lobby.seats.every((s) => s !== null && s.ready);
  const seatsFilled = lobby.seats.every((s) => s !== null);
  const isHost = me?.id === lobby.hostId;
  const shareUrl = `${location.origin}/join/${lobby.joinCode}`;
  // Per-game lobby options come from the game registry — no game-specific
  // logic lives in this file anymore.
  const game = gameByKind(lobby.gameKind);
  const LobbyOptions = game?.LobbyOptions;
  const seatOptions = seatOptionsFor(lobby.gameKind);
  // The seat-count blurb leans on the per-game registry blurb. The lone
  // game-specific exception is Sheng Ji's Find-the-Friend / fixed-teams
  // hint, since that depends on the live seat count + config.
  const friendModeForced =
    lobby.gameKind === "shengji" && lobby.seatCount % 2 !== 0;
  const findTheFriend =
    lobby.gameKind === "shengji" &&
    (friendModeForced || lobby.config.friendMode === "find_the_friend");
  const teamHint =
    lobby.gameKind === "shengji"
      ? findTheFriend
        ? `Find-the-Friend: the declarer secretly calls ${Math.max(
            1,
            Math.floor((lobby.seatCount - 2) / 2)
          )} card(s) — whoever plays them joins the declarer's team.`
        : `Fixed teams: seats 0, 2${lobby.seatCount === 6 ? ", 4" : ""} vs ${
            lobby.seatCount === 6 ? "1, 3, 5" : "1, 3"
          }.`
      : null;

  return (
    <main className="container">
      {ws.lastError && <div className="toast">{ws.lastError}</div>}
      <div className="card-block">
        <div className="row between lobby-header">
          <div className="row" style={{ gap: 12, alignItems: "center", minWidth: 0 }}>
            <GameKindBadge kind={lobby.gameKind} size={44} />
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0 }} className="lobby-title">{lobby.name}</h2>
              <p className="muted" style={{ margin: "4px 0 0" }}>
                <span className={`pill ${lobby.visibility}`}>{lobby.visibility}</span>{" "}
                {GAME_LABELS[lobby.gameKind]} · {lobby.seats.filter((s) => s !== null).length} / {lobby.seatCount} seats
              </p>
            </div>
          </div>
          <div className="row lobby-header-actions" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <RulesButton gameKind={lobby.gameKind} />
            {isHost ? (
              <label className="row inline-control" style={{ gap: 6 }}>
                <span className="muted">Game</span>
                <Select<GameKind>
                  value={lobby.gameKind}
                  onChange={(k) => ws.send({ t: "set_game_kind", gameKind: k })}
                  options={GAME_KINDS.map((k) => ({ value: k, label: GAME_LABELS[k] }))}
                  title="Switch the game — clears game-specific settings and un-readies all players"
                  width={200}
                  compact
                />
              </label>
            ) : null}
            <button onClick={() => { ws.send({ t: "leave_lobby" }); navigate("/"); }}>Leave</button>
          </div>
        </div>

        <p className="muted lobby-blurb">{GAME_BLURBS[lobby.gameKind]}</p>

        {LobbyOptions && (
          <LobbyOptions lobby={lobby} isHost={isHost} send={ws.send} />
        )}
      </div>

      <div className="card-block">
        <h3>Share</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          Send this link to your friends — opens directly into this lobby.
        </p>
        <div className="row" style={{ gap: 6 }}>
          <input readOnly value={shareUrl} onClick={(e) => (e.target as HTMLInputElement).select()} />
          <button onClick={() => navigator.clipboard.writeText(shareUrl)}>Copy</button>
        </div>
      </div>

      <div className="card-block">
        <div className="row between" style={{ alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0 }}>Seats</h3>
          <label className="row inline-control" style={{ gap: 6 }}>
            <span className="muted" style={{ fontSize: 13 }}>Player count</span>
            <Select<number>
              value={lobby.seatCount}
              onChange={(n) => ws.send({ t: "set_seat_count", seatCount: n })}
              disabled={!isHost || seatOptions.length === 1}
              options={seatOptions.map((n) => ({ value: n, label: String(n) }))}
              title={
                isHost
                  ? seatOptions.length === 1
                    ? `${GAME_LABELS[lobby.gameKind]} is fixed at ${seatOptions[0]} players`
                    : ""
                  : "Only the host can resize the lobby"
              }
              width={80}
              compact
            />
          </label>
        </div>
        {teamHint && (
          <p className="muted" style={{ marginTop: 6 }}>{teamHint}</p>
        )}
        <div className="seat-card-grid">
          {lobby.seats.map((s, i) => {
            const myThisSeat = s?.playerId === me?.id;
            const isBot = s?.kind === "bot";
            const filled = s !== null;
            const ready = filled && (isBot || s!.ready);
            const offline = filled && !isBot && !s!.connected;
            const barClass = !filled ? "empty" : ready ? "ready" : "not-ready";
            return (
              <div key={i} className="seat-card">
                <div className="seat-card-top">
                  <div className="seat-card-number">{i + 1}</div>
                  <div className="seat-card-name">
                    {filled ? (
                      <>
                        <Name
                          className="seat-card-name-text"
                          displayName={s!.displayName}
                          discriminator={s!.discriminator}
                        />
                        {isBot && <span className="seat-card-bot-tag">BOT</span>}
                        {offline && <span className="seat-card-off-tag">offline</span>}
                      </>
                    ) : (
                      <span className="muted">empty</span>
                    )}
                  </div>
                </div>
                <div className="seat-card-actions">
                  {!filled && (
                    <>
                      {!seated && (
                        <button
                          className="primary seat-card-btn"
                          onClick={() => ws.send({ t: "take_seat", seatIndex: i })}
                        >
                          Sit
                        </button>
                      )}
                      <button
                        className="seat-card-btn"
                        onClick={() => ws.send({ t: "add_bot", seatIndex: i })}
                      >
                        Add bot
                      </button>
                    </>
                  )}
                  {isBot && (
                    <button
                      className="seat-card-btn"
                      onClick={() => ws.send({ t: "remove_bot", seatIndex: i })}
                    >
                      Remove bot
                    </button>
                  )}
                  {myThisSeat && (
                    <>
                      <button
                        className="seat-card-btn"
                        onClick={() => ws.send({ t: "set_ready", ready: !s!.ready })}
                      >
                        {s!.ready ? "Unready" : "Ready"}
                      </button>
                      <button
                        className="seat-card-btn"
                        onClick={() => ws.send({ t: "leave_seat" })}
                      >
                        Leave seat
                      </button>
                    </>
                  )}
                </div>
                <div className={`seat-card-ready-bar ${barClass}`} />
              </div>
            );
          })}
        </div>
        <div className="row between" style={{ marginTop: 14 }}>
          <div className="muted">
            {seatsFilled ? (allReady ? "All players ready." : "Waiting for everyone to ready up.") : "Waiting for players."}
          </div>
          <button
            className="primary"
            disabled={!isHost || !allReady || !seatsFilled}
            onClick={() => ws.send({ t: "start_game" })}
            title={isHost ? "" : "Only the host can start"}
          >
            Start Game
          </button>
        </div>
      </div>

      <div className="card-block">
        <h3>Players in lobby ({lobby.members.length})</h3>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Anyone in the lobby who isn't seated when the game starts will
          spectate (they won't see anyone's hand).
        </p>
        <div className="lobby-list">
          {lobby.members.map((m) => (
            <div key={m.playerId} className="lobby-row">
              <div className="meta">
                <Name
                  className="name"
                  displayName={m.displayName}
                  discriminator={m.discriminator}
                />
                {m.isHost && <span className="pill">HOST</span>}
                {m.seated && <span className="pill public">Seat {m.seatIndex}</span>}
                {!m.seated && <span className="pill">spectator</span>}
                {m.playerId === me?.id && <span className="pill private">YOU</span>}
              </div>
              {isHost && m.playerId !== me?.id && (
                <button
                  className="danger"
                  onClick={() => ws.send({ t: "kick_player", playerId: m.playerId })}
                >
                  Kick
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-block">
        <h3>Chat</h3>
        <ChatPanel
          messages={ws.chat}
          myId={me?.id ?? null}
          onSend={(text) => ws.send({ t: "chat", text })}
        />
      </div>
    </main>
  );
}
